import React, { useMemo, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BREAKPOINTS } from '@/hooks/useBreakpoint';
import DataTableToolbar, { type DataTableToolbarProps } from './DataTableToolbar';
import ResizableHeaderCell from './ResizableHeaderCell';
import { useColumnWidths } from './useColumnWidths';
import {
  clearTablePreferences,
  useColumnVisibility,
  useTableSort,
  type TableSortState,
} from './useTablePreferences';
import { compareSortValues } from '@/utils/tableSort';
import DataTableShell from './DataTableShell';
import DataTableScrollArea from './DataTableScrollArea';

export type DataTableColumn<T> = {
  id: string;
  header: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  align?: 'left' | 'center' | 'right';
  resizable?: boolean;
  hidden?: boolean;
  hideable?: boolean;
  actions?: boolean;
  sortable?: boolean;
  sortValue?: (row: T) => string | number | Date | null | undefined;
  csvValue?: (row: T) => string | number | null | undefined;
  className?: string;
  render: (row: T) => React.ReactNode;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  emptyState?: React.ReactNode;
  columnStorageKey?: string;
  getRowClassName?: (row: T) => string | undefined;
  className?: string;
  stickyHeader?: boolean;
  defaultSort?: TableSortState;
  onSortChange?: (sort: TableSortState) => void;
  clientSort?: boolean;
  toolbar?: Omit<DataTableToolbarProps, 'columnsMenu'>;
  onRowClick?: (row: T) => void;
  /** Render stacked cards instead of table on narrow viewports. Default true. */
  responsiveCards?: boolean;
  /** Optional custom mobile card renderer (receives row + action controls). */
  renderMobileCard?: (row: T, actions: React.ReactNode) => React.ReactNode;
  /** Optional row selection for bulk actions. */
  selection?: {
    isSelected: (key: string) => boolean;
    onToggleRow: (key: string) => void;
    pageKeys: string[];
    pageAllSelected: boolean;
    pageSomeSelected: boolean;
    onTogglePage: () => void;
  };
};

function isHideable<T>(col: DataTableColumn<T>): boolean {
  if (col.actions) return false;
  if (col.hideable === false) return false;
  return true;
}

export default function DataTable<T>({
  columns,
  rows,
  getRowKey,
  emptyState,
  columnStorageKey,
  getRowClassName,
  className = '',
  stickyHeader = true,
  defaultSort,
  onSortChange,
  clientSort = true,
  toolbar,
  onRowClick,
  responsiveCards = true,
  renderMobileCard,
  selection,
}: DataTableProps<T>) {
  const baseColumns = useMemo(() => columns.filter((c) => !c.hidden), [columns]);

  const hideableIds = useMemo(
    () => baseColumns.filter(isHideable).map((c) => c.id),
    [baseColumns]
  );

  const sortableIds = useMemo(
    () => baseColumns.filter((c) => c.sortable && c.sortValue).map((c) => c.id),
    [baseColumns]
  );

  const { hiddenIds, toggleColumn, resetVisibility } = useColumnVisibility(
    columnStorageKey,
    hideableIds
  );

  const { sort, setSort, resetSort } = useTableSort(columnStorageKey, sortableIds, defaultSort);

  const displayColumns = useMemo(
    () => baseColumns.filter((c) => !isHideable(c) || !hiddenIds.has(c.id)),
    [baseColumns, hiddenIds]
  );

  const widthDefs = useMemo(
    () =>
      displayColumns.map((c) => ({
        id: c.id,
        width: c.width ?? 120,
        minWidth: c.minWidth,
        maxWidth: c.maxWidth,
      })),
    [displayColumns]
  );

  const storageKey = columnStorageKey ? `data-table:${columnStorageKey}` : undefined;
  const { widths, setWidth, resetWidth, resetAllWidths } = useColumnWidths(widthDefs, storageKey);

  const [internalSearch, setInternalSearch] = useState('');
  const enableSearch = Boolean(toolbar?.searchPlaceholder || toolbar?.onSearchChange);
  const searchValue = toolbar?.searchValue ?? internalSearch;
  const onSearchChangeHandler =
    toolbar?.onSearchChange ?? (enableSearch ? setInternalSearch : undefined);

  const visibleHideableCount = useMemo(
    () => hideableIds.filter((id) => !hiddenIds.has(id)).length,
    [hideableIds, hiddenIds]
  );

  const filteredRows = useMemo(() => {
    let next = rows;
    const query = searchValue?.trim().toLowerCase();
    if (query) {
      next = next.filter((row) =>
        baseColumns.some((col) => {
          if (col.actions) return false;
          const value = col.sortValue?.(row) ?? col.csvValue?.(row);
          return value != null && String(value).toLowerCase().includes(query);
        })
      );
    }
    if (clientSort && sort) {
      const col = baseColumns.find((c) => c.id === sort.columnId);
      if (col?.sortValue) {
        next = [...next].sort((a, b) =>
          compareSortValues(col.sortValue!(a), col.sortValue!(b), sort.direction)
        );
      }
    }
    return next;
  }, [rows, searchValue, baseColumns, clientSort, sort]);

  function handleToggleColumn(id: string) {
    toggleColumn(id, visibleHideableCount);
  }

  function handleSort(columnId: string) {
    const next =
      !sort || sort.columnId !== columnId
        ? { columnId, direction: 'asc' as const }
        : sort.direction === 'asc'
          ? { columnId, direction: 'desc' as const }
          : null;
    setSort(next);
    onSortChange?.(next);
  }

  function handleResetColumns() {
    resetVisibility();
    resetSort();
    resetAllWidths();
    if (columnStorageKey) clearTablePreferences(columnStorageKey);
  }

  const showColumnsMenu = Boolean(columnStorageKey && hideableIds.length > 0);
  const showToolbar = Boolean(toolbar) || showColumnsMenu;
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.tabletMax}px)`);
  const showMobileCards = responsiveCards && isMobile;

  const actionColumn = useMemo(
    () => displayColumns.find((c) => c.actions),
    [displayColumns],
  );
  const cardColumns = useMemo(
    () => displayColumns.filter((c) => !c.actions),
    [displayColumns],
  );

  return (
    <DataTableShell>
      {showToolbar && (
        <DataTableToolbar
          {...toolbar}
          searchValue={searchValue}
          onSearchChange={onSearchChangeHandler}
          searchPlaceholder={toolbar?.searchPlaceholder}
          columnsMenu={
            showColumnsMenu
              ? {
                  columns: hideableIds.map((id) => {
                    const col = baseColumns.find((c) => c.id === id)!;
                    return {
                      id,
                      header: col.header,
                      visible: !hiddenIds.has(id),
                      hideable: true,
                    };
                  }),
                  onToggle: handleToggleColumn,
                  onReset: handleResetColumns,
                }
              : undefined
          }
        />
      )}
      <DataTableScrollArea className={className} sticky={stickyHeader}>
        {!showMobileCards && (
        <table className="admin-table data-table">
          <colgroup>
            {displayColumns.map((col) => (
              <col key={col.id} style={{ width: widths[col.id] ?? col.width ?? 120 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selection ? (
                <th className="data-table__select-header" scope="col">
                  <input
                    type="checkbox"
                    aria-label="Select all rows on page"
                    checked={selection.pageAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = selection.pageSomeSelected;
                    }}
                    onChange={selection.onTogglePage}
                  />
                </th>
              ) : null}
              {displayColumns.map((col) => (
                <ResizableHeaderCell
                  key={col.id}
                  width={widths[col.id] ?? col.width ?? 120}
                  minWidth={col.minWidth}
                  maxWidth={col.maxWidth}
                  resizable={col.resizable !== false && !col.actions}
                  align={col.actions ? 'right' : col.align}
                  sortable={Boolean(col.sortable && col.sortValue && clientSort)}
                  sortDirection={sort?.columnId === col.id ? sort.direction : null}
                  onSort={() => handleSort(col.id)}
                  className={
                    col.actions
                      ? ['data-table__actions-header', col.className].filter(Boolean).join(' ')
                      : col.className
                  }
                  onResize={(w) => setWidth(col.id, w)}
                  onReset={() => resetWidth(col.id)}
                >
                  {col.header}
                </ResizableHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={displayColumns.length + (selection ? 1 : 0)}>{emptyState}</td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const rowKey = getRowKey(row);
                return (
                <tr
                  key={rowKey}
                  className={[
                    'data-table__body-row',
                    getRowClassName?.(row),
                    onRowClick ? 'data-table__row--clickable' : '',
                    selection?.isSelected(rowKey) ? 'data-table__row--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selection ? (
                    <td
                      className="data-table__select-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label="Select row"
                        checked={selection.isSelected(rowKey)}
                        onChange={() => selection.onToggleRow(rowKey)}
                      />
                    </td>
                  ) : null}
                  {displayColumns.map((col) => (
                    <td
                      key={col.id}
                      className={[
                        col.actions ? 'actions data-table__actions' : '',
                        col.className,
                        col.align === 'right' ? 'data-table__td--right' : '',
                        col.align === 'center' ? 'data-table__td--center' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
              })
            )}
          </tbody>
        </table>
        )}
      </DataTableScrollArea>
      {showMobileCards && filteredRows.length === 0 && emptyState ? (
        <div className="data-table-mobile-empty">{emptyState}</div>
      ) : null}
      {showMobileCards && filteredRows.length > 0 && (
        <div className="data-table-mobile-cards" aria-label="Table rows">
          {filteredRows.map((row) => {
            const rowKey = getRowKey(row);
            const actions = actionColumn ? actionColumn.render(row) : null;
            if (renderMobileCard) {
              return (
                <div key={rowKey} className="data-table-mobile-card data-table-mobile-card--custom">
                  {renderMobileCard(row, actions)}
                </div>
              );
            }
            return (
            <article
              key={rowKey}
              className={[
                'data-table-mobile-card',
                getRowClassName?.(row),
                onRowClick ? 'data-table-mobile-card--clickable' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {cardColumns.map((col) => (
                <div key={col.id} className="data-table-mobile-card__row">
                  <span className="data-table-mobile-card__label">{col.header}</span>
                  <span className="data-table-mobile-card__value">{col.render(row)}</span>
                </div>
              ))}
              {actionColumn ? (
                <div className="data-table-mobile-card__actions">{actionColumn.render(row)}</div>
              ) : null}
            </article>
            );
          })}
        </div>
      )}
    </DataTableShell>
  );
}
